import { observable, action, decorate, computed } from "mobx";
import { isEqual, omit, cloneDeep } from "lodash";
import { datadogRum } from "@datadog/browser-rum";

import filterToolsAndProducts from "utils/FilterToolsAndProducts";
import convertLanguage from "utils/convertLanguage";
import { isCustomerSupportV2Enabled } from "utils/supportButton";

/** Class representing global state of the app.
 * Store is decorated with mobx.
 * It uses the following mobx and mobx-persist decorators:
 * - observable: variable is set as observable (observers will be notified if the variable is updated)
 * - persist: variable will be persisted in a storage
 * - action(.bound): the function is enabled to update the store. Its "this" is bound to the store.
 * - computed: after an observable update, this function automatically updates a complex variable
 * @see {@link https://mobx.js.org/|mobx}
 * @see {@link https://www.npmjs.com/package/mobx-persist|mobx-persist}
 */
class Store {
  /**
   * initializes store.
   * @constructor
   */
  constructor() {
    this.loginProjectName = "";
    this.projectName = null;
    this.hostKey = null;
    this.projectId = null;
    this.logo = [];
    this.assetsUrl = null;
    this.enabledTools = [];
    this.snackbarMessage = undefined;
    this.snackbarError = false;
    this.vendorEmail = "";
    this.errorModal = false;
    this.loggedUser = null;
    this.project_id = undefined;
    this.pageTitles = undefined;
    this.modalOtp = false;
    this.products = [];
    this.forcingLogout = false;
    this.checkingJwt = null;
    this.languages = undefined;
    this.baseUrl = undefined;
    this.area = "";
    this.defaultLang = "";
    this.isCustomerSupportV2Enabled = false;
  }

  /* ACTIONS */
  setBaseUrl(val) {
    this.baseUrl = val;
  }

  /**
   * Hydrates store state from the bootstrap project payload returned by `getProjectInfoByHost`.
   *
   * This is also the place where the raw customer support project flag is normalized into the
   * boolean used by the UI to decide between `v2`, `legacy`, or `hidden` support modes.
   *
   * @param {Object} obj Project bootstrap payload.
   */
  setAssetsByObject(obj) {
    if (obj) {
      this.loginProjectName = obj.displayName;
      this.projectName = obj.name;
      this.hostKey = obj.hostKey;
      this.projectId = obj.id;
      this.project_id = obj.id;
      this.assetsUrl = obj.assetsUrl;
      this.logo = obj.logo;
      this.pageTitles = obj.pageTitles;
      this.enabledTools = obj.enabledTools;
      this.baseUrl = obj.baseurl;
      this.languages = obj.businessPlatformConfig
        ? obj.businessPlatformConfig.languages
        : undefined;
      this.area = obj.area;
      this.defaultLang = obj.defaultLang;
      this.isCustomerSupportV2Enabled = isCustomerSupportV2Enabled(
        obj.projectFlags?.customerSupport,
      );
    }
  }

  setProducts(value, toolStatus) {
    if (toolStatus) {
      const products = [];
      value.forEach((tool) => {
        const status = toolStatus.filter((ts) => filterToolsAndProducts(ts, tool))[0];
        if (status) {
          const myTool = cloneDeep(tool);
          myTool.status = status.status;
          products.push(myTool);
        }
      });

      this.products = [...products];
    } else {
      this.products = [...value];
    }
  }

  /**
   * sets the logged user
   * @param {Object} userData the logged user object
   */
  setLoggedUser(userData, withCheckOTP = false) {
    if (
      !this.loggedUser ||
      !isEqual(omit(this.loggedUser, ["language", "TwoFA"]), omit(userData, ["language", "TwoFA"]))
    ) {
      this.loggedUser = userData;
    }
    if (userData.firstLogin && !userData.TwoFA && withCheckOTP) {
      this.setModalOtp(true);
    }
    if (process.env.REACT_APP_NAMESPACE === "biz-tecma-prod") {
      datadogRum.setUser({
        id: userData.id,
        name: userData.firstName,
        surname: userData.lastName,
        email: userData.email,
        role: userData.role,
      });
    }
  }

  clearLoggedUser() {
    this.loggedUser = null;
    this.checkingJwt = false;
  }

  setLoggedUserFirstLogin(value) {
    this.loggedUser.firstLogin = value;
  }
  setLoggedUserTwoFA(value) {
    this.loggedUser.TwoFA = value;
  }

  setModalOtp(value) {
    // Body scroll-lock while this modal is open is owned entirely by
    // @tecma/ds's Modal component (it captures/restores document.body.style.overflow
    // itself). Setting it here too raced with that logic: this ran before the
    // Modal ever mounted, so its "capture the original value" step captured
    // "hidden" (written here) instead of the page's true original value,
    // leaving scroll permanently locked after close. See TECMA-5745.
    this.modalOtp = value;
  }

  /**
   * sets the logged user language
   * @param {Object} userData the logged user object
   */
  setLoggedUserLang(language, locale) {
    if (language) {
      this.loggedUser.language = language;
    }
    if (locale) {
      this.loggedUser.locale = locale;
    }
  }

  /**
   * set the language of the system
   * @param {string} val
   */
  setForcingLogout(val) {
    this.forcingLogout = val;
  }

  setCheckingJwt(val) {
    this.checkingJwt = val;
  }

  /**
   * sets the loging in vendor email
   * @param {string} val the email
   */
  updateVendorEmail(val) {
    if (this.vendorEmail !== val) {
      this.vendorEmail = val;
    }
  }

  /**
   * open/close error modal
   * @param {boolean} val
   */
  setErrorModal(val) {
    this.errorModal = val;
  }
  setSnackbarError(err, mes) {
    if (err !== this.snackbarError) {
      this.snackbarMessage = mes;
      this.snackbarError = err;
    }
  }

  /* COMPUTED */

  /**
   * computes the logged user full name
   */
  get loggedUserFullname() {
    if (this.loggedUser && this.loggedUser.firstName && this.loggedUser.lastName) {
      return `${this.loggedUser.firstName} ${this.loggedUser.lastName}`;
    } else if (this.loggedUser && this.loggedUser.id) {
      return this.loggedUser.id;
    } else {
      return "-";
    }
  }

  get configLanguages() {
    if (Array.isArray(this.languages)) {
      return this.languages.map((lang) => {
        return convertLanguage(lang);
      });
    }
    return ["it-IT", "en-GB"];
  }
}

decorate(Store, {
  snackbarMessage: observable,
  snackbarError: observable,
  modalOtp: observable,
  loginProjectName: observable,
  projectName: observable,
  hostKey: observable,
  projectId: observable,
  project_id: observable,
  assetsUrl: observable,
  area: observable,
  defaultLang: observable,
  logo: observable,
  pageTitles: observable,
  enabledTools: observable,
  products: observable,
  loggedUser: observable,
  forcingLogout: observable,
  checkingJwt: observable,
  languages: observable,
  baseUrl: observable,
  isCustomerSupportV2Enabled: observable,
  vendorEmail: observable,
  setAssetsByObject: action.bound,
  updateVendorEmail: action.bound,
  setLoggedUser: action.bound,
  clearLoggedUser: action.bound,
  setLoggedUserFirstLogin: action.bound,
  setLoggedUserTwoFA: action.bound,
  setModalOtp: action.bound,
  setLoggedUserLang: action.bound,
  setErrorModal: action.bound,
  setSnackbarError: action.bound,
  loggedUserFullname: computed,
  configLanguages: computed,
  setForcingLogout: action.bound,
  setCheckingJwt: action.bound,
  setBaseUrl: action.bound,
});

export default Store;
